import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Facebook, Instagram, RefreshCcw, Link as LinkIcon, Unlink, ExternalLink, CheckCircle2, AlertCircle } from 'lucide-react';
import { integrationsApi, clientsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const IntegrationsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const [metaStatus, setMetaStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [assignDialog, setAssignDialog] = useState(null); // account being reassigned

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, accountsRes, clientsRes] = await Promise.all([
        integrationsApi.getMetaConfigStatus(),
        integrationsApi.getSocialAccounts(),
        clientsApi.getAll()
      ]);
      setMetaStatus(statusRes.data);
      setAccounts(accountsRes.data || []);
      setClients(clientsRes.data || []);
    } catch (err) {
      toast.error('Error cargando integraciones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Handle OAuth callback redirects
  useEffect(() => {
    const success = searchParams.get('meta_success');
    const errorMsg = searchParams.get('meta_error');
    const pages = searchParams.get('pages');
    if (success) {
      toast.success(`Conexion exitosa: ${pages || 0} pagina(s) vinculada(s)`);
      setSearchParams({});
      fetchAll();
    } else if (errorMsg) {
      toast.error(`Error de Meta: ${errorMsg}`);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, fetchAll]);

  const handleConnectMeta = async () => {
    if (!isAdmin) {
      toast.error('Solo administradores pueden conectar cuentas');
      return;
    }
    setConnecting(true);
    try {
      const r = await integrationsApi.startMetaOAuth();
      // Redirect browser to Facebook OAuth
      window.location.href = r.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo iniciar la conexion');
      setConnecting(false);
    }
  };

  const handleDisconnect = async (pageId, pageName) => {
    if (!window.confirm(`Desconectar "${pageName}"? Ya no podras publicar a esa pagina hasta volver a conectarla.`)) return;
    try {
      await integrationsApi.disconnectAccount(pageId);
      toast.success('Cuenta desconectada');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleAssignClient = async (clientId) => {
    if (!assignDialog) return;
    try {
      await integrationsApi.assignClient(assignDialog.page_id, clientId || null);
      toast.success('Cuenta asignada');
      setAssignDialog(null);
      fetchAll();
    } catch (err) {
      toast.error('Error al asignar');
    }
  };

  const clientName = (cid) => clients.find(c => c.client_id === cid)?.name || 'Sin asignar';

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto p-6" data-testid="integrations-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Integraciones de redes sociales</h1>
          <p className="text-muted-foreground mt-1">Conecta las paginas de Facebook e Instagram de tus clientes para publicar directamente desde el calendario.</p>
        </div>

        {/* Meta Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Facebook className="text-blue-500" size={24} />
                </div>
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Meta (Facebook + Instagram)
                    {metaStatus?.configured ? (
                      <Badge className="bg-green-500/15 text-green-600 border-green-500/30"><CheckCircle2 size={12} className="mr-1" /> Listo</Badge>
                    ) : (
                      <Badge variant="destructive"><AlertCircle size={12} className="mr-1" /> No configurado</Badge>
                    )}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">Publica posts, reels, carruseles y stories en Pages e Instagram Business.</p>
                </div>
              </div>
              {isAdmin && metaStatus?.configured && (
                <Button onClick={handleConnectMeta} disabled={connecting} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="connect-meta-btn">
                  <LinkIcon size={14} className="mr-1" />
                  {accounts.length > 0 ? 'Agregar mas paginas' : 'Conectar Facebook & Instagram'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Cargando...</p>
            ) : accounts.length === 0 ? (
              <div className="text-center py-8 border border-dashed rounded-lg">
                <Facebook className="mx-auto text-muted-foreground/50 mb-3" size={40} />
                <p className="text-sm text-muted-foreground mb-3">No hay paginas conectadas todavia</p>
                <p className="text-xs text-muted-foreground max-w-md mx-auto">
                  Conectate con tu cuenta de Facebook que administra las paginas de tus clientes.
                  Podras elegir cuales conectar y vincular cada una al cliente correspondiente.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {accounts.map((acc) => (
                  <div key={acc.page_id} className="flex items-start justify-between border rounded-lg p-4 hover:bg-accent/30 transition-colors" data-testid={`account-card-${acc.page_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-1">
                          <Facebook size={14} className="text-blue-500" />
                          <span className="font-semibold">{acc.page_name}</span>
                        </div>
                        {acc.ig_user_id && (
                          <Badge variant="outline" className="text-pink-500 border-pink-500/30 gap-1">
                            <Instagram size={12} />
                            @{acc.ig_username || acc.ig_user_id}
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs">{acc.status === 'active' ? 'Activa' : acc.status}</Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span>Cliente: <strong className="text-foreground">{clientName(acc.client_id)}</strong></span>
                        <span>Page ID: {acc.page_id}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-3">
                      <Button variant="outline" size="sm" onClick={() => setAssignDialog(acc)} data-testid={`assign-client-${acc.page_id}`}>
                        Asignar cliente
                      </Button>
                      {isAdmin && (
                        <Button variant="outline" size="sm" onClick={() => handleDisconnect(acc.page_id, acc.page_name)} className="text-red-600 border-red-500/30 hover:bg-red-500/10" data-testid={`disconnect-${acc.page_id}`}>
                          <Unlink size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {accounts.length > 0 && (
              <div className="mt-4 pt-4 border-t flex items-center justify-between text-xs text-muted-foreground">
                <span>{accounts.length} pagina(s) conectada(s)</span>
                <Button variant="ghost" size="sm" onClick={fetchAll}>
                  <RefreshCcw size={12} className="mr-1" /> Actualizar
                </Button>
              </div>
            )}
            
            {/* App Review notice */}
            {metaStatus?.configured && (
              <div className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                <strong>Estado App Review:</strong> Mientras tu app de Meta no este en modo Live (despues de pasar App Review),
                solo podras conectar cuentas de testers que hayas agregado en el dashboard de Meta. Otros usuarios veran error de permisos.
                <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 ml-2 underline">
                  Panel de Meta <ExternalLink size={10} />
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        {/* TikTok Card (placeholder for later) */}
        <Card className="opacity-60">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-zinc-800/10">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z"/></svg>
                </div>
                <div>
                  <CardTitle className="flex items-center gap-2">TikTok <Badge variant="outline">Proximamente</Badge></CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">Programado para Fase 3 - requiere aprobacion de Content Posting API.</p>
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Assign Client Dialog */}
        <Dialog open={!!assignDialog} onOpenChange={(open) => !open && setAssignDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Asignar a un cliente</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Selecciona el cliente al que pertenece la pagina <strong>{assignDialog?.page_name}</strong>.
              </p>
              <div className="space-y-1 max-h-72 overflow-y-auto">
                <button
                  onClick={() => handleAssignClient(null)}
                  className="w-full text-left px-3 py-2 rounded hover:bg-accent text-sm"
                >
                  <span className="text-muted-foreground italic">Sin asignar (general)</span>
                </button>
                {clients.map(c => (
                  <button
                    key={c.client_id}
                    onClick={() => handleAssignClient(c.client_id)}
                    className="w-full text-left px-3 py-2 rounded hover:bg-accent text-sm flex items-center justify-between"
                    data-testid={`assign-to-${c.client_id}`}
                  >
                    <span>{c.name}</span>
                    {assignDialog?.client_id === c.client_id && <CheckCircle2 size={14} className="text-green-500" />}
                  </button>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default IntegrationsPage;
